
import React, { createContext, useReducer, useContext, useEffect } from 'react';

const SearchHeaderContext = createContext();

const initialState = {
  products: [],
  filters: [],
  activeFilters: {},
  sortOption: 'RELEVANCE',
  loading: false,
  totalResults: 0,
  currentPage: 1,
  isLastPage: false,
  productsPerPage: 8,
  searchQuery: ''
};

const actionTypes = {
  SET_PRODUCTS: 'SET_PRODUCTS',
  SET_FILTERS: 'SET_FILTERS',
  SET_ACTIVE_FILTERS: 'SET_ACTIVE_FILTERS',
  SET_SORT_OPTION: 'SET_SORT_OPTION',
  SET_LOADING: 'SET_LOADING',
  SET_TOTAL_RESULTS: 'SET_TOTAL_RESULTS',
  SET_CURRENT_PAGE: 'SET_CURRENT_PAGE',
  SET_IS_LAST_PAGE: 'SET_IS_LAST_PAGE',
  SET_PRODUCTS_PER_PAGE: 'SET_PRODUCTS_PER_PAGE',
  SET_SEARCH_QUERY: 'SET_SEARCH_QUERY',
};

function searchHeaderReducer(state, action) {
  switch (action.type) {
    case actionTypes.SET_PRODUCTS:
      return { ...state, products: action.payload };
    case actionTypes.SET_FILTERS:
      return { ...state, filters: action.payload };
    case actionTypes.SET_ACTIVE_FILTERS:
      return { ...state, activeFilters: action.payload };
    case actionTypes.SET_SORT_OPTION:
      return { ...state, sortOption: action.payload };
    case actionTypes.SET_LOADING:
      return { ...state, loading: action.payload };
    case actionTypes.SET_TOTAL_RESULTS:
      return { ...state, totalResults: action.payload };
    case actionTypes.SET_CURRENT_PAGE:
      return { ...state, currentPage: action.payload };
    case actionTypes.SET_IS_LAST_PAGE:
      return { ...state, isLastPage: action.payload };
    case actionTypes.SET_PRODUCTS_PER_PAGE:
      return { ...state, productsPerPage: action.payload };
    case actionTypes.SET_SEARCH_QUERY:
      return { ...state, searchQuery: action.payload };
    default:
      return state;
  }
}

export const SearchHeaderProvider = ({ children, defaultSettings = {} }) => {
  const [state, dispatch] = useReducer(searchHeaderReducer, {
    ...initialState,
    productsPerPage: defaultSettings.productsPerPage || initialState.productsPerPage,
    sortOption: defaultSettings.defaultSortOption || initialState.sortOption,
  });

  useEffect(() => {
    const handleSearchComplete = (event) => {
      const { products, totalResults, query } = event.detail;
      if (event.detail.type == 'header-search') {
        dispatch({ type: actionTypes.SET_PRODUCTS, payload: products });
        dispatch({ type: actionTypes.SET_TOTAL_RESULTS, payload: totalResults });
        dispatch({ type: actionTypes.SET_SEARCH_QUERY, payload: query });
        dispatch({ type: actionTypes.SET_LOADING, payload: false });
      }
    };

    const handleSearchInitiated = (event) => {
      const { page, productsPerPage } = event.detail;
      if (event.detail.type == 'header-search') {
        dispatch({ type: actionTypes.SET_CURRENT_PAGE, payload: page || 1 });
        dispatch({ type: actionTypes.SET_PRODUCTS_PER_PAGE, payload: productsPerPage || initialState.productsPerPage });
        dispatch({ type: actionTypes.SET_LOADING, payload: true });
        dispatch({ type: actionTypes.SET_IS_LAST_PAGE, payload: false });
      }
    };

    const handleFilterApplied = (event) => {
      const { filters_object, type } = event.detail;
      if (type === 'header-search') {
        dispatch({ type: actionTypes.SET_LOADING, payload: true });
      }
    };

    // Listen for searchQuery changes and reset to default if empty
    let lastSearchQuery = '';
    const handleSearchQueryChange = () => {
      const searchQuery = window?.searchHeaderContext?.searchQuery || '';
      if (searchQuery !== lastSearchQuery) {
        lastSearchQuery = searchQuery;
        if (!searchQuery || searchQuery.length === 0) {
          // Reset products/categories to default (popular products, categories, etc)
          dispatch({ type: actionTypes.SET_PRODUCTS, payload: [] });
          dispatch({ type: actionTypes.SET_TOTAL_RESULTS, payload: 0 });
          dispatch({ type: actionTypes.SET_LOADING, payload: false });
        }
      }
    };
    window.addEventListener('searchrequest', handleSearchInitiated);
    window.addEventListener('searchrequest-products-complete', handleSearchComplete);
    window.addEventListener('searchrequest-filter-applied', handleFilterApplied);
    window.addEventListener('search-header-query-change', handleSearchQueryChange);
    return () => {
      window.removeEventListener('searchrequest-products-complete', handleSearchComplete);
      window.removeEventListener('searchrequest', handleSearchInitiated);
      window.removeEventListener('searchrequest-filter-applied', handleFilterApplied);
      window.removeEventListener('search-header-query-change', handleSearchQueryChange);
    };
  }, []);

  return (
    <SearchHeaderContext.Provider value={{ ...state, dispatch, actionTypes }}>
      {children}
    </SearchHeaderContext.Provider>
  );
};

export const useSearchHeader = () => {
  const context = useContext(SearchHeaderContext);
  if (!context) {
    throw new Error('useSearchHeader must be used within a SearchHeaderProvider');
  }
  return context;
};

export default SearchHeaderContext;